import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ArrowRight, Loader2 } from "lucide-react";
import SEO from "@/components/SEO";
import api from "@/lib/api";
import { toast } from "sonner";
import { trackEvent } from "@/components/Analytics";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k,v) => setForm(f=>({...f,[k]:v}));

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      trackEvent("contact_form_submit", { subject: form.subject || "general" });
      toast.success("Message received");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send");
    } finally { setSubmitting(false); }
  }

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact Park Home Support | UK Specialist Support"
        description="Send us the details of your park home issue and we'll point you in the right direction. advice@parkhomesupport.co.uk · 0333 577 6047."
        canonical="https://parkhomesupport.co.uk/contact"
      />
      <section className="hero-gradient section-pad">
        <div className="container-phs">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <span className="eyebrow">Contact</span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.05] text-[#071A33]">
                Need Help With a <span className="text-[#064E2E]">Park Home Issue?</span>
              </h1>
              <p className="mt-6 text-lg text-[#334155]">Send us the details and we'll point you in the right direction.</p>

              <ul className="mt-10 space-y-5 text-[#071A33]">
                <li className="flex items-start gap-3"><Mail className="text-[#064E2E] mt-1" size={20}/><div><p className="font-medium">Email</p><p className="text-[#475569]">advice@parkhomesupport.co.uk</p></div></li>
                <li className="flex items-start gap-3"><Phone className="text-[#064E2E] mt-1" size={20}/><div><p className="font-medium">Phone</p><a href="tel:+443335776047" className="text-[#475569] hover:text-[#064E2E] transition" data-testid="contact-phone-link">0333 577 6047</a></div></li>
                <li className="flex items-start gap-3"><MapPin className="text-[#064E2E] mt-1" size={20}/><div><p className="font-medium">UK-wide</p><p className="text-[#475569]">Specialist park home support, England, Scotland, Wales</p></div></li>
              </ul>
            </div>

            <div className="lg:col-span-7">
              {done ? (
                <div className="card-default text-center" data-testid="contact-confirmation">
                  <h2 className="font-display text-3xl text-[#071A33]">Thanks — we've received your message</h2>
                  <p className="mt-3 text-[#334155]">We'll respond shortly. If you've already submitted a request, our specialists will pick it up first.</p>
                  <Link to="/" className="btn-outline mt-6 inline-flex">Back to Home</Link>
                </div>
              ) : (
                <form className="card-default" onSubmit={onSubmit} data-testid="contact-form">
                  <h2 className="font-display text-2xl text-[#071A33]">Request Support</h2>
                  <p className="text-sm text-[#475569] mt-1">Tell us what's happening and we'll get back to you with the right next step.</p>
                  <div className="grid sm:grid-cols-2 gap-5 mt-6">
                    <Field label="Name" required><input className="field-input" required value={form.name} onChange={e=>update("name", e.target.value)} data-testid="ct-name"/></Field>
                    <Field label="Email" required><input className="field-input" type="email" required value={form.email} onChange={e=>update("email", e.target.value)} data-testid="ct-email"/></Field>
                    <Field label="Phone"><input className="field-input" value={form.phone} onChange={e=>update("phone", e.target.value)} data-testid="ct-phone"/></Field>
                    <Field label="Subject" required><input className="field-input" required value={form.subject} onChange={e=>update("subject", e.target.value)} data-testid="ct-subject"/></Field>
                  </div>
                  <div className="mt-5">
                    <label className="field-label">Message <span className="text-[#B88A2A]">*</span></label>
                    <textarea className="field-input" required value={form.message} onChange={e=>update("message", e.target.value)} data-testid="ct-message"/>
                  </div>
                  <button disabled={submitting} className="btn-primary mt-6" data-testid="ct-submit-btn">
                    {submitting ? <><Loader2 className="animate-spin" size={16}/> Sending…</> : <>Request Support <ArrowRight size={16}/></>}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="field-label">{label}{required ? <span className="text-[#B88A2A]"> *</span> : null}</label>
      {children}
    </div>
  );
}
